import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { AlarmClock, Loader2 } from "lucide-react";

function fmtDue(iso) {
  try {
    return new Date(iso).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function TasksTodayWidget() {
  const { state } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/tasks/today");
        if (alive) setData(data);
      } catch {
        if (alive) setData({ total: 0, overdue: 0, items: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  const overdue = data?.overdue || 0;
  const total = data?.total || 0;

  return (
    <Card
      className={`border ${
        overdue > 0
          ? "badge-error border"
          : "border-slate-800 bg-slate-900/40"
      }`}
      data-testid="tasks-today-widget"
    >
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <AlarmClock className={`h-4 w-4 ${overdue > 0 ? "text-error-text" : "text-[#C4553B]"}`} />
            <span className="text-sm font-semibold text-slate-200">Mes tâches du jour</span>
          </div>
          {total > 0 && (
            <span className="tabular text-xs text-slate-400" data-testid="tasks-today-count">
              {total} · <span className={overdue > 0 ? "text-error-text" : ""}>{overdue} en retard</span>
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : total === 0 ? (
          <div className="py-3 text-center text-xs text-slate-500" data-testid="tasks-today-empty">
            Aucune tâche pour aujourd&apos;hui. 🎯
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="tasks-today-list">
            {(data.items || []).slice(0, 6).map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-sm">
                <span className={`h-1.5 w-1.5 rounded-full ${t.is_overdue ? "bg-[#E67B6D]" : "bg-[#C4553B]"}`} />
                <Link
                  to={`/leads/${t.lead_id}`}
                  className="flex-1 truncate text-slate-200 hover:text-[#F0C572]"
                  data-testid={`tasks-today-item-${t.id}`}
                >
                  {t.title}
                </Link>
                <span className={`text-[11px] ${t.is_overdue ? "text-[#E67B6D]" : "text-slate-500"}`}>
                  {fmtDue(t.due_at)}
                </span>
              </li>
            ))}
            {(data.items || []).length > 6 && (
              <li className="pt-1 text-center text-[11px] italic text-slate-500">
                +{data.items.length - 6} autres
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
